import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  DynamicPageHeader,
  DynamicPageTitle,
  ObjectPage,
  ObjectPageSection,
  Panel,
  FlexBox,
  FlexBoxDirection,
  Label,
  ObjectStatus,
  ValueState,
  FlexBoxWrap,
  Title,
  BusyIndicatorSize,
  Form,
  FormItem,
  Text,
  TitleLevel,
  Bar,
  Button,
  BarDesign,
  ButtonDesign,
  FlexBoxJustifyContent,
  ComboBox,
  ComboBoxItem,
  Select,
  TextArea,
  Option,
  WrappingType,
  FlexBoxAlignItems,
  MessageBoxTypes,
  DatePicker,
  Switch
} from '@ui5/webcomponents-react';
import { useStyles } from './DisplayQuery.jss';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  BaseQuery,
  DistributionStatus,
  ForwardReview,
  IssueType,
  Maybe,
  ResourceAccessLevel,
  Status,
  useGetQueryDetailsQuery,
  useGetUserInfoQuery,
  useSendEmailNotificationMutation
} from 'generated/graphql';
import { Loading } from 'components/shared/Loading';
import { Attachments } from './Attachments/Attachments';
import { QueryConversation } from './QueryConversation';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { DisplayQueryDetails } from './DisplayQueryDetails/DisplayQueryDetails';
import { useUpdateBaseQueryStore } from 'stores/UpdateBaseQueryStore';
import { SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import {
  validateDeadline,
  validateAssigneeEmail,
  validateString,
  validateEmail
} from 'utils/validator';
import { Prompt } from 'react-router-dom';
import { CombinedError } from '@urql/core';
import { NotFoundPage } from 'components/shared/ErrorPage/NotFoundPage';
import { compareAlphabetically } from 'utils/sorting';
import { env } from 'config/env';
import '@ui5/webcomponents-icons/dist/email';
import { DiscontinueQuery } from './QueryActions/DiscontinueQuery';
import { EditQuery } from './QueryActions/EditQuery';
import { useGetDate } from 'hooks/useGetDate';
import { DistributionPanel } from './Panels/Distribution';
import { ConfirmationDialog } from 'components/shared/ConfirmationDialog';
import { CompleteQuery } from './QueryActions/CompleteQuery';
import { ClarifyQuery } from './QueryActions/ClarifyQuery';
import { ResultUserMessage, UserMessage } from 'components/shared/UserMessage';
import dayjs from 'dayjs';
import { DistributeQuery } from './QueryActions/DistributeQuery';
import { ForwardQuery } from './QueryActions/ForwardQuery';
import { useEmailTemplate } from 'hooks/useEmailTemplate';
import { useUserViewStore } from 'stores/UserViewStore';
import { CopyEmailContentToClipboardDialog } from 'components/ManageQueries/AssignDialog/CopyEmailContentToClipboardDialog/CopyEmailContentToClipboardDialog';
import { getEncodedMailtoTemplate, isAbleToSendNotificationEmail } from 'utils/notificationEmail';
import { MsalEmailInputSearch } from 'components/shared/MsalEmailInputSearch';
import { filterAllowedIssueTypes } from 'utils/filterAllowedIssueTypes';
import {
  getProjectManager,
  getQualityExpert,
  getQueryResponsible,
  getSubstituteProjectManager
} from 'utils/projectStakeholders';
import { formatUserName } from 'utils/formatUserName';
import { DeleteQuery } from './QueryActions/DeleteQuery/DeleteQuery';
import { OverflowMenu } from 'components/shared/OverflowMenu/OverflowMenu';
import { DeletedQueryPage } from './DeletedQueryPage/DeletedQueryPage';

export interface DisplayQueryParams {
  queryId: string;
}

type FormDisplayItemBase = {
  data?: string | Maybe<string>;
  dataTestId: string;
  i18nTag: string;
  notification?: boolean;
  required?: boolean;
  renderNotEditableComponent?: (entry: FormDisplayItem) => ReactElement;
};

type EditableComponent = FormDisplayItemBase & {
  editable?: true;
  renderComponent: () => ReactElement;
};

type NonEditableComponent = FormDisplayItemBase & {
  editable: false;
};

export type FormDisplayItem = EditableComponent | NonEditableComponent;

export const DisplayQuery: React.FC = () => {
  const { t } = useTranslation();
  const { queryId } = useParams<DisplayQueryParams>();
  const {
    editMode,
    allowCancelEditing,
    allowEditModeWithoutConfirmation,
    setAllowEditModeWithoutConfirmation,
    toggleEditMode,
    setCancelEditing
  } = useEditQueryStore();
  const baseQueryStore = useUpdateBaseQueryStore();
  const classes = useStyles({ editMode });
  const { getFormattedDateString, getEarliestDate } = useGetDate();

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isUserMessageOpen, setIsUserMessageOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState<ResultUserMessage>({
    isSuccess: false,
    message: t('display-query-edit-query-save-error')
  });

  const specificRef = useRef<SpecificQueryType>(null);
  const assigneeChanged = useRef<boolean>(false);
  const [{ data: userData }] = useGetUserInfoQuery({ requestPolicy: 'network-only' });
  const [{ data: getQueryData, fetching: fetchingQueryData, error: errorFetchingQueryDetails }] =
    useGetQueryDetailsQuery({
      requestPolicy: 'network-only',
      variables: { id: parseInt(queryId) }
    });
  const [, sendEmail] = useSendEmailNotificationMutation();
  const { getEmailTemplate } = useEmailTemplate();

  const hasLanguagesTranslatorTeams =
    (getQueryData?.query?.queryDetails?.__typename === 'AbapQuery' ||
      getQueryData?.query?.queryDetails?.__typename === 'DitaQuery' ||
      getQueryData?.query?.queryDetails?.__typename === 'NonAbapQuery') &&
    getQueryData?.query?.queryDetails?.languagesTranslatorTeams;
  const distributionLanguages = hasLanguagesTranslatorTeams
    ? getQueryData.query?.distributions.map(({ language }) => language)
    : getQueryData?.query?.project.languages;
  const setShouldUseSessionVariant = useUserViewStore((state) => state.setShouldUseSessionVariant);

  const [hasExceededEmailLengthLimit, setHasExceededEmailLengthLimit] = useState<boolean>(false);
  const sendWithBackend: boolean = env?.REACT_APP_EMAIL_VIA_BACKEND === 'true' ? true : false;
  const accessLevel = getQueryData?.query?.createdBy.accessLevel;

  const sortedDistributions = useMemo(
    () =>
      getQueryData?.query?.distributions.sort((lang1, lang2) =>
        compareAlphabetically(lang1.language.language, lang2.language.language)
      ),
    [getQueryData?.query?.distributions]
  );

  const checkAccess = (
    element: JSX.Element,
    allowedLevels: ResourceAccessLevel[]
  ): JSX.Element | null => {
    if (accessLevel && allowedLevels.includes(accessLevel)) {
      return element;
    }
    return null;
  };

  useEffect(() => {
    fillQueryDataFromQuery();
  }, [getQueryData?.query]);

  useEffect(() => {
    // use the session's selected view when going back to manage queries page
    setShouldUseSessionVariant(true);

    if (editMode) {
      toggleEditMode();
    }
    return () => baseQueryStore.resetAllData();
  }, []);

  useEffect(() => {
    if (!allowCancelEditing) {
      window.addEventListener('beforeunload', leavingPageAlert);
      return () => {
        window.removeEventListener('beforeunload', leavingPageAlert);
      };
    }
    window.removeEventListener('beforeunload', leavingPageAlert);
    return undefined;
  }, [allowCancelEditing]);

  const fillQueryDataFromQuery = () => {
    if (getQueryData && getQueryData.query) {
      baseQueryStore.setInitialStateWithProps(getQueryData.query);
    }
  };

  const leavingPageAlert = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };

  const validateAllBaseFields = () => {
    const isSourceTextValid = validateSourceText();
    const isTargetLanguageValid = validateTargetLanguage();
    const isIssueTypeValid = validateIssueType();
    const isAssigneeValid = validateAssignee();
    return (
      isSourceTextValid &&
      isTargetLanguageValid &&
      isIssueTypeValid &&
      isAssigneeValid &&
      isDeadlineValid()
    );
  };

  const validateSourceText = () => {
    return validateString(baseQueryStore.sourceText, baseQueryStore.setSourceTextValueState);
  };

  const validateAssignee = () => {
    return validateAssigneeEmail(
      getQueryData?.query?.assignee ?? '',
      baseQueryStore.assignee,
      baseQueryStore.setAssigneeValueState
    );
  };

  const validateTargetLanguage = () => {
    if (
      getLanguagesFilteredByCreator().find(
        (language: string) => language === baseQueryStore.targetLanguage
      )
    ) {
      baseQueryStore.setTargetLanguageValueState(ValueState.Success);
      return true;
    } else {
      baseQueryStore.setTargetLanguageValueState(ValueState.Error);
      return false;
    }
  };

  const isDeadlineValid = () => {
    if (
      baseQueryStore.deadlineValueState !== ValueState.Error &&
      dayjs(baseQueryStore.deadline).isValid()
    ) {
      return true;
    }
    return false;
  };

  const validateIssueType = () => {
    return validateString(baseQueryStore.selectedIssueType, baseQueryStore.setIssueTypeValueState);
  };
  const onChangeSourceText = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLTextAreaElement;
    baseQueryStore.setSourceText(value);
    validateString(value, baseQueryStore.setSourceTextValueState);
  };

  const onChangeTargetText = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLTextAreaElement;
    baseQueryStore.setTargetText(value);
  };

  const onChangeTargetLanguage = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    baseQueryStore.setTargetLanguage(value);
    validateString(value, baseQueryStore.setTargetLanguageValueState);
  };

  const onChangeIssueType = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.detail.selectedOption.dataset;
    baseQueryStore.setSelectedIssueType(value);
    validateString(value, baseQueryStore.setIssueTypeValueState);
  };

  const onChangeDetails = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLTextAreaElement;
    baseQueryStore.setDetails(value);
  };

  const onChangeAssignee = (email: string) => {
    setCancelEditing(false);
    baseQueryStore.setAssignee(email);
    const isEmailValid = validateEmail(email);
    const isEmailDifferentFromStore = email !== baseQueryStore.assignee;
    if (isEmailValid && isEmailDifferentFromStore) {
      assigneeChanged.current = true;
    }
  };

  const onChangeQEReview = (event: CustomEvent) => {
    const { checked } = event.target as HTMLInputElement;
    baseQueryStore.setQEReview(checked);
  };

  const onChangeDeadline = (event: CustomEvent) => {
    event.preventDefault();
    const { value, valid } = event.detail as any;
    baseQueryStore.setDeadline(value);
    validateDeadline(baseQueryStore.setDeadlineValueState, value, valid);
  };

  //the onInputDeadline was created to cover the validation of the deadline field
  // as it is not being triggered all times for when the field is empty
  const onInputDeadline = (event: CustomEvent) => {
    event.preventDefault();
    const { value, valid } = event.detail as any;
    validateDeadline(baseQueryStore.setDeadlineValueState, value, valid);
  };

  const showSaveResultMessage = (combinedError?: CombinedError) => {
    if (!combinedError) {
      setResultMessage({
        isSuccess: true,
        message: t('display-query-edit-query-save-successfully')
      });
    } else {
      setResultMessage({
        isSuccess: false,
        message: t('display-query-edit-query-save-error')
      });
    }
    setIsUserMessageOpen(true);
  };

  const showFieldValidationMessage = () => {
    setResultMessage({
      isSuccess: false,
      message: t('create-query-check-input-toast-message')
    });
    setIsUserMessageOpen(true);
  };

  const showEmailNotificationMessage = (success: boolean) => {
    if (success) {
      setResultMessage({
        isSuccess: true,
        message: t('email-success')
      });
    } else {
      setResultMessage({
        isSuccess: false,
        message: t('email-fail')
      });
    }
    setIsUserMessageOpen(true);
  };

  const sendEmailNotification = async (email: string) => {
    if (assigneeChanged.current && email && sendWithBackend) {
      const query = getQueryData?.query as BaseQuery;
      const href = location.href;
      const subject = t('email-subject');
      const body = getEmailTemplate(query, href);
      const { data } = await sendEmail({
        email: {
          to: email,
          body,
          subject
        }
      });
      showEmailNotificationMessage(data?.result as boolean);
      assigneeChanged.current = false;
    }
  };

  const queryResponsible = getQueryResponsible(getQueryData?.query || undefined);
  const substituteProjectManager = getSubstituteProjectManager(getQueryData?.query?.project);
  const qualityEngineer = getQualityExpert(getQueryData?.query?.project);
  const projectManager = getProjectManager(getQueryData?.query?.project);

  const onNotificationButtonClick = () => {
    const queryAssignee = baseQueryStore.assignee.trim();

    const uri = getEncodedMailtoTemplate(queryAssignee, getBody(), t('email-subject'));

    if (isAbleToSendNotificationEmail(uri) && queryAssignee) location.assign(uri);
    else setHasExceededEmailLengthLimit(true);
  };

  const getBody = () => {
    const query = getQueryData?.query as BaseQuery;
    const href = `${location.origin}/queries/${queryId}`;

    return getEmailTemplate(query, href);
  };

  const getNotificationButton = (): JSX.Element | null => {
    if (!sendWithBackend && isInternalUser()) {
      const assignee = baseQueryStore.assignee.trim();
      return (
        <Button
          icon="email"
          tooltip={t('email-button')}
          aria-label={t('email-button')}
          design={ButtonDesign.Emphasized}
          onClick={onNotificationButtonClick}
          className={classes.notificationButton}
          disabled={editMode || !assignee}
          data-help-id="assignee-email-button"
        />
      );
    }
    return null;
  };

  const isDistributionCompleted = (status: DistributionStatus) => {
    return status !== DistributionStatus.DRAFT && status !== DistributionStatus.PENDING;
  };

  const enableDistributionConfirmationDialog = async () => {
    const completedDistributionsToRemove =
      sortedDistributions?.filter(
        (distribution) =>
          baseQueryStore.resetedDistributions.includes(distribution.id) &&
          isDistributionCompleted(distribution.status)
      ) || [];
    if (completedDistributionsToRemove.length > 0) {
      setIsConfirmationDialogOpen(true);
    } else {
      await saveEditQuery();
    }
  };

  const saveEditQuery = async () => {
    if (specificRef.current?.isValid() && validateAllBaseFields()) {
      const saveResult = await specificRef.current?.save();
      setCancelEditing(true);
      toggleEditMode();
      if (saveResult.error) {
        baseQueryStore.setInitialStateWithProps(getQueryData!.query!);
        specificRef.current?.cancel();
      } else {
        sendEmailNotification(baseQueryStore.assignee);
      }
      showSaveResultMessage(saveResult?.error);
    } else {
      showFieldValidationMessage();
    }
  };

  const cancelEditQuery = () => {
    let confirmAnswer = false;
    if (!allowCancelEditing) {
      confirmAnswer = confirm(t('display-query-edit-confirm-cancel-edit'));
    }
    if (allowCancelEditing || confirmAnswer) {
      baseQueryStore.setInitialStateWithProps(getQueryData!.query!);
      specificRef.current?.cancel();
      toggleEditMode();
      setCancelEditing(true);
    }
  };

  const renderHeaderStatus = () => {
    return (
      <FlexBox
        data-testid="header-status-flexbox"
        data-help-id="display-query-header-status"
        direction={FlexBoxDirection.Column}
      >
        <Label id="status-label" className={classes.headerLabel}>
          {t('display-query-header-label-status')}
        </Label>
        <ObjectStatus
          data-testid="header-status-status"
          aria-labelledby="status-label"
          className={classes.headerValue}
          state={ValueState.None}
        >
          {t(baseQueryStore.selectedStatus as string)}
        </ObjectStatus>
      </FlexBox>
    );
  };

  const userFromAgency = () => {
    return userData?.user?.suppliers?.some((supplier) => {
      return supplier.name === getQueryData?.query?.submitterGroupName;
    });
  };

  const deadlineMinDate = () => {
    return dayjs(getQueryData?.query?.createdAt).add(1, 'day').toString();
  };

  const isStatusEqual = (status: Status) => baseQueryStore.selectedStatus === status;

  const shouldDisplayOverflowMenu = () => {
    return !editMode && (hasPermissionToDiscontinueQuery() || hasPermissionToDeleteQuery());
  };

  const hasPermissionToDeleteQuery = () => isInternalUser();

  const hasPermissionToDiscontinueQuery = () => {
    const externalUserAllowed =
      userFromAgency() &&
      (isStatusEqual(Status.NEW) ||
        isStatusEqual(Status.ASSIGNED) ||
        isStatusEqual(Status.CREATOR_TO_CLARIFY));
    return isInternalUser() || externalUserAllowed;
  };

  const hasPermissionToEditQuery = () => {
    const externalUserAllowed =
      userFromAgency() &&
      (isStatusEqual(Status.NEW) ||
        isStatusEqual(Status.ASSIGNED) ||
        isStatusEqual(Status.CREATOR_TO_CLARIFY));
    return isInternalUser() || externalUserAllowed;
  };

  const hasPermissionToDistributeQuery = () => {
    return (
      isInternalUser() &&
      baseQueryStore.qeReview &&
      isStatusEqual(Status.ANSWERED) &&
      baseQueryStore.distributions.length > 0
    );
  };

  const isInternalUser = () => {
    return userData?.user?.internal ?? false;
  };

  const getSuppliersFromUser = () => {
    return userData?.user?.suppliers.flatMap((supplier) => [
      t('supplier-name-format', { name: supplier.name, vendor: supplier.vendor }),
      supplier.vendor
    ]);
  };

  const hasNoDraftDistributionToUserSupplier = (): boolean => {
    const suppliersFromUser = getSuppliersFromUser();
    return (
      getQueryData?.query?.distributions?.some(
        (distribution) =>
          distribution.status !== DistributionStatus.DRAFT &&
          suppliersFromUser?.includes(distribution.language.supplier)
      ) || false
    );
  };

  const hasPermissionToCompleteQuery = () => {
    return isInternalUser() || hasNoDraftDistributionToUserSupplier();
  };

  const shouldShowClarifyQuery = () => {
    const isUserInternal = isInternalUser();
    if (isUserInternal) {
      return (
        isStatusEqual(Status.CREATOR_TO_CLARIFY) ||
        (isStatusEqual(Status.PENDING_REVIEW) &&
          getQueryData?.query?.forwardReviewTo === ForwardReview.CREATOR)
      );
    }
    if (accessLevel === ResourceAccessLevel.SAME_AGENCY) {
      return isStatusEqual(Status.CREATOR_TO_CLARIFY);
    }
    return false;
  };

  const renderHeaderContent = () => {
    return (
      <DynamicPageHeader className={classes.header} data-testid="display-query-header">
        <FlexBox wrap={FlexBoxWrap.Wrap} justifyContent={FlexBoxJustifyContent.SpaceBetween}>
          <FlexBox wrap={FlexBoxWrap.Wrap} className={classes.headerFlexBoxGroup}>
            {renderHeaderStatus()}
            <FlexBox
              data-help-id="display-query-header-project"
              direction={FlexBoxDirection.Column}
            >
              <Label id="project-label" className={classes.headerLabel}>
                {t('display-query-header-label-project')}
              </Label>
              <ObjectStatus
                aria-labelledby="project-label"
                className={classes.headerValue}
                state={ValueState.None}
              >
                {getQueryData?.query?.project?.projectName}
              </ObjectStatus>
            </FlexBox>
          </FlexBox>
          <FlexBox className={classes.actionGroup}>
            <div
              hidden={editMode || isStatusEqual(Status.COMPLETED) || isStatusEqual(Status.CLOSED)}
            >
              <ForwardQuery
                id={queryId}
                status={getQueryData?.query?.status}
                forwardReviewTo={getQueryData?.query?.forwardReviewTo}
                user={userData?.user}
              />
              {shouldShowClarifyQuery() && (
                <ClarifyQuery
                  resourceAccessLevel={accessLevel}
                  query={getQueryData?.query}
                  setResultMessage={setResultMessage}
                  setIsUserMessageOpen={setIsUserMessageOpen}
                />
              )}
            </div>
            <div hidden={editMode}>
              {hasPermissionToCompleteQuery() && (
                <CompleteQuery
                  id={queryId}
                  status={getQueryData?.query?.status}
                  user={userData?.user}
                  distributions={sortedDistributions}
                />
              )}
            </div>
            <div
              hidden={editMode || isStatusEqual(Status.COMPLETED) || isStatusEqual(Status.CLOSED)}
            >
              {hasPermissionToDistributeQuery() && (
                <DistributeQuery
                  id={queryId}
                  setResultMessage={setResultMessage}
                  setIsUserMessageOpen={setIsUserMessageOpen}
                />
              )}
              {hasPermissionToEditQuery() && (
                <>
                  <EditQuery
                    toggleEditMode={toggleEditMode}
                    allowEditModeWithoutConfirmation={allowEditModeWithoutConfirmation}
                    setAllowEditModeWithoutConfirmation={setAllowEditModeWithoutConfirmation}
                  />
                </>
              )}
            </div>
            {shouldDisplayOverflowMenu() && (
              <OverflowMenu
                data-testid="query-details-overflow-menu-button"
                data-help-id="query-details-overflow-menu-button"
                className={classes.actionGroupItem}
              >
                <FlexBox
                  direction={FlexBoxDirection.Column}
                  justifyContent={FlexBoxJustifyContent.SpaceBetween}
                  style={{ rowGap: '0.3rem' }}
                >
                  {hasPermissionToDeleteQuery() && (
                    <DeleteQuery
                      id={queryId}
                      setResultMessage={setResultMessage}
                      setIsUserMessageOpen={setIsUserMessageOpen}
                    />
                  )}
                  {hasPermissionToDiscontinueQuery() && (
                    <DiscontinueQuery
                      id={queryId}
                      setResultMessage={setResultMessage}
                      setIsUserMessageOpen={setIsUserMessageOpen}
                    />
                  )}
                </FlexBox>
              </OverflowMenu>
            )}
          </FlexBox>
        </FlexBox>
      </DynamicPageHeader>
    );
  };

  const getLanguagesFilteredByCreator = (): string[] => {
    const distinctLanguages = new Set(
      getQueryData?.query?.languagesByQueryCreator
        .filter((language) => !!language)
        .map((language) => language!.language)
    );
    return Array.from(distinctLanguages);
  };

  const questionInformationData: FormDisplayItem[] = [
    {
      data: baseQueryStore.targetLanguage,
      dataTestId: 'target-language',
      renderComponent: () => (
        <ComboBox
          data-testid="edit-target-language"
          data-help-id="edit-target-language"
          onChange={onChangeTargetLanguage}
          value={baseQueryStore.targetLanguage}
          className={classes.formLineInput}
          valueState={baseQueryStore.targetLanguageValueState}
          valueStateMessage={
            <Label wrappingType={WrappingType.Normal}>
              {t('display-value-state-message-target-language')}
            </Label>
          }
        >
          {getLanguagesFilteredByCreator()
            .sort(compareAlphabetically)
            .map((language: string) => (
              <ComboBoxItem
                key={language}
                text={language}
                data-testid="target-language-combobox-item"
              />
            ))}
        </ComboBox>
      ),
      i18nTag: t('display-query-question-info-label-target-language'),
      required: true
    },
    {
      data: getFormattedDateString(baseQueryStore.deadline),
      dataTestId: 'deadline',
      renderComponent: () => (
        <DatePicker
          data-testid="edit-deadline"
          className={classes.formLineInput}
          valueState={baseQueryStore.deadlineValueState}
          valueStateMessage={
            <Label wrappingType={WrappingType.Normal}>
              {t('display-value-state-message-deadline')}
            </Label>
          }
          value={dayjs(baseQueryStore.deadline).format('MMM D, YYYY')}
          onChange={onChangeDeadline}
          onInput={onInputDeadline}
          minDate={getFormattedDateString(deadlineMinDate())}
        />
      ),
      i18nTag: t('display-query-question-info-label-deadline'),
      required: true
    },
    {
      data: t(baseQueryStore.selectedIssueType),
      dataTestId: 'issue-type',
      renderComponent: () => (
        <Select
          data-testid="edit-issue-type"
          data-help-id="edit-issue-type"
          onChange={onChangeIssueType}
          className={classes.formLineInput}
          valueState={baseQueryStore.issueTypeValueState}
          valueStateMessage={
            <Label wrappingType={WrappingType.Normal}>
              {t('display-value-state-message-issue-type')}
            </Label>
          }
          title={t(baseQueryStore.selectedIssueType)}
        >
          {Object.values(IssueType)
            .filter(
              (issue) =>
                getQueryData &&
                getQueryData.query &&
                filterAllowedIssueTypes(getQueryData.query.translationType)(issue)
            )
            .sort((a, b) => compareAlphabetically(t(a), t(b)))
            .map((issueType) => (
              <Option
                key={issueType}
                data-value={issueType}
                selected={issueType === (baseQueryStore.selectedIssueType as unknown as IssueType)}
              >
                {t(issueType)}
              </Option>
            ))}
        </Select>
      ),
      i18nTag: t('display-query-question-info-label-issue-type'),
      required: true
    },
    {
      data: baseQueryStore.sourceText,
      dataTestId: 'source-text',
      renderComponent: () => (
        <TextArea
          data-testid="edit-source-text"
          data-help-id="edit-source-text"
          onChange={onChangeSourceText}
          rows={4}
          value={baseQueryStore.sourceText || ''}
          valueState={baseQueryStore.sourceTextValueState}
          valueStateMessage={
            <Label wrappingType={WrappingType.Normal}>
              {t('display-value-state-message-source-text')}
            </Label>
          }
        />
      ),
      i18nTag: t('display-query-question-info-label-source-text'),
      required: true
    },
    {
      data: baseQueryStore.targetText,
      dataTestId: 'target-text',
      renderComponent: () => (
        <TextArea
          data-testid="edit-target-text"
          data-help-id="edit-target-text"
          onChange={onChangeTargetText}
          rows={4}
          value={baseQueryStore.targetText || ''}
        />
      ),
      i18nTag: t('display-query-question-info-label-target-text')
    },
    {
      data: baseQueryStore.details,
      dataTestId: 'details',
      renderComponent: () => (
        <TextArea
          data-testid="edit-details"
          data-help-id="edit-details"
          onChange={onChangeDetails}
          value={baseQueryStore.details}
          rows={4}
        />
      ),
      i18nTag: t('display-query-question-info-label-details')
    }
  ];

  const getQueryProjectDeadline = () => {
    const targetProjectLanguagesDeadlines = getQueryData?.query?.project.languages
      .filter(
        (projectLanguage) =>
          projectLanguage?.language === baseQueryStore.targetLanguage && projectLanguage.deadline
      )
      .map((language) => language?.deadline);

    if (targetProjectLanguagesDeadlines && targetProjectLanguagesDeadlines.length > 0) {
      return getEarliestDate(targetProjectLanguagesDeadlines);
    }

    const allDeadlines = getQueryData?.query?.project.languages
      .filter((projectLanguage) => projectLanguage?.deadline)
      .map((projectLanguage) => projectLanguage?.deadline);

    if (allDeadlines && allDeadlines.length > 0) {
      return getEarliestDate(allDeadlines);
    }

    return getQueryData?.query?.project.deadline;
  };

  const projectDeadline = useMemo(
    () => getQueryProjectDeadline(),
    [getQueryData?.query?.project, baseQueryStore.targetLanguage]
  );

  const renderQuestionPanel = () => {
    return (
      <div
        data-help-id="display-query-panel-information"
        data-testid="display-query-panel-information"
      >
        <Panel
          aria-labelledby="information-panel"
          header={
            <Title id="information-panel" level={TitleLevel.H4}>
              {t('display-query-title-question-information')}
            </Title>
          }
          fixed={true}
        >
          <Form
            labelSpanXL={3}
            labelSpanL={2}
            labelSpanM={3}
            labelSpanS={12}
            className={classes.formComponent}
          >
            {questionInformationData.map((infoData) => (
              <FormItem
                key={infoData.dataTestId}
                label={
                  <Label
                    id={infoData.i18nTag}
                    required={editMode && infoData.required}
                    data-help-id={infoData.dataTestId}
                    className={classes.formItemLabel}
                    wrappingType={WrappingType.Normal}
                  >
                    {infoData.i18nTag}
                  </Label>
                }
              >
                {editMode && infoData.editable !== false ? (
                  infoData.renderComponent()
                ) : (
                  <FlexBox
                    alignItems={FlexBoxAlignItems.Center}
                    className={classes.infoDataFlexBox}
                  >
                    {infoData.data && (
                      <Text
                        data-testid={infoData.dataTestId}
                        aria-labelledby={infoData.i18nTag}
                        className={classes.formItemText}
                        wrapping
                      >
                        {infoData.data}
                      </Text>
                    )}
                  </FlexBox>
                )}
              </FormItem>
            ))}
          </Form>
        </Panel>
      </div>
    );
  };

  const renderDisplayQueryDetails = () => (
    <div data-help-id="display-query-panel-details">
      <Panel
        aria-labelledby="details-panel"
        className={classes.formContainer}
        header={
          <Title id="query-detail-panel" level={TitleLevel.H4}>
            {t('display-query-details-title-details')}
          </Title>
        }
      >
        <Form
          labelSpanXL={3}
          labelSpanL={2}
          labelSpanM={3}
          labelSpanS={12}
          aria-label="display-query-details-form"
          className={classes.formComponent}
        >
          <DisplayQueryDetails
            queryDetails={getQueryData?.query?.queryDetails}
            queryId={queryId}
            ref={specificRef}
          />
        </Form>
      </Panel>
    </div>
  );

  const renderAdministrativeDetailsPanel = () => {
    return (
      <div data-help-id="display-query-panel-administrative">
        <Panel
          aria-labelledby="administrative-panel"
          className={classes.formContainer}
          header={
            <Title id="administrative-panel" level={TitleLevel.H4}>
              {t('display-query-title-administrative-details')}
            </Title>
          }
          data-testid="display-query-title-administrative-details"
        >
          <Form
            labelSpanXL={3}
            labelSpanL={2}
            labelSpanM={3}
            labelSpanS={12}
            className={classes.formComponent}
          >
            {getQueryData?.query?.qtId && (
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-query-tracker-id')}
                    data-help-id="display-query-adm-details-query-tracker-id"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-query-tracker-id')}
                  </Label>
                }
              >
                <Text
                  aria-labelledby={t('display-query-adm-details-query-tracker-id')}
                  className={classes.formItemText}
                  data-testid="query-tracker-id"
                  wrapping
                >
                  {getQueryData?.query?.qtId}
                </Text>
              </FormItem>
            )}
            <FormItem
              label={
                <Label
                  id={t('display-query-adm-details-submitter-group-name')}
                  data-help-id="display-query-adm-details-submitter-group-name-label"
                  className={classes.formItemLabel}
                >
                  {t('display-query-adm-details-submitter-group-name')}
                </Label>
              }
            >
              <Text
                aria-labelledby={t('display-query-adm-details-submitter-group-name')}
                className={classes.formItemText}
                data-testid="submitter-group-name"
                wrapping
              >
                {getQueryData?.query?.submitterGroupName}
              </Text>
            </FormItem>
            {checkAccess(
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-query-submitter')}
                    data-help-id="display-query-adm-details-query-submitter-label"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-query-submitter')}
                  </Label>
                }
              >
                <Text
                  aria-labelledby={t('display-query-adm-details-query-submitter')}
                  className={classes.formItemText}
                  data-testid="query-submitter"
                  wrapping
                >
                  {formatUserName({
                    userName: getQueryData?.query?.createdBy?.displayName,
                    sapNumber: getQueryData?.query?.createdBy?.sapNumber ?? undefined,
                    defaultValue: getQueryData?.query?.createdBy?.displayName
                  })}
                </Text>
              </FormItem>,
              [ResourceAccessLevel.INTERNAL, ResourceAccessLevel.SAME_AGENCY]
            )}
            <FormItem
              label={
                <Label
                  id={t('display-query-adm-details-query-createat')}
                  data-help-id="display-query-adm-details-query-createat-label"
                  className={classes.formItemLabel}
                >
                  {t('display-query-adm-details-query-createat')}
                </Label>
              }
            >
              <Text
                aria-labelledby={t('display-query-adm-details-query-createat')}
                className={classes.formItemText}
                data-testid="query-createat"
                wrapping
              >
                {getFormattedDateString(getQueryData?.query?.createdAt, true)}
              </Text>
            </FormItem>
            {checkAccess(
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-project-deadline')}
                    data-help-id="display-query-adm-details-project-deadline"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-project-deadline')}
                  </Label>
                }
              >
                <Text
                  aria-labelledby={t('display-query-adm-details-project-deadline')}
                  className={classes.formItemText}
                  data-testid="project-deadline"
                  wrapping
                >
                  {projectDeadline ? getFormattedDateString(projectDeadline) : ''}
                </Text>
              </FormItem>,
              [ResourceAccessLevel.INTERNAL]
            )}
            {checkAccess(
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-qe-name')}
                    data-help-id="display-query-adm-details-qe-name-label"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-qe-name')}
                  </Label>
                }
              >
                <Text
                  aria-labelledby={t('display-query-adm-details-qe-name')}
                  className={classes.formItemText}
                  data-testid="quality-expert"
                  wrapping
                >
                  {qualityEngineer}
                </Text>
              </FormItem>,
              [ResourceAccessLevel.INTERNAL]
            )}
            {checkAccess(
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-pm-name')}
                    data-help-id="display-query-adm-details-pm-name-label"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-pm-name')}
                  </Label>
                }
              >
                <Text
                  aria-labelledby={t('display-query-adm-details-pm-name')}
                  className={classes.formItemText}
                  data-testid="quality-expert"
                  wrapping
                >
                  {projectManager}
                </Text>
              </FormItem>,
              [ResourceAccessLevel.INTERNAL]
            )}
            {checkAccess(
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-query-responsible')}
                    data-help-id="display-query-adm-details-query-responsible-label"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-query-responsible')}
                  </Label>
                }
              >
                <Text
                  aria-labelledby={t('display-query-adm-details-query-responsible')}
                  className={classes.formItemText}
                  data-testid="quality-expert"
                  wrapping
                >
                  {queryResponsible}
                </Text>
              </FormItem>,
              [ResourceAccessLevel.INTERNAL]
            )}
            {checkAccess(
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-substitute-pm')}
                    data-help-id="display-query-adm-details-substitute-pm-label"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-substitute-pm')}
                  </Label>
                }
              >
                <Text
                  aria-labelledby={t('display-query-adm-details-substitute-pm')}
                  className={classes.formItemText}
                  data-testid="substitute-pm"
                  wrapping
                >
                  {substituteProjectManager}
                </Text>
              </FormItem>,
              [ResourceAccessLevel.INTERNAL]
            )}
            {checkAccess(
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-assignee')}
                    data-help-id="display-query-adm-details-assignee-label"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-assignee')}
                  </Label>
                }
              >
                {editMode ? (
                  <MsalEmailInputSearch
                    aria-labelledby={t('display-query-adm-details-assignee')}
                    data-testid="edit-assignee"
                    data-help-id="edit-assignee"
                    className={classes.formLineInput}
                    value={baseQueryStore.assignee}
                    onEmailChange={onChangeAssignee}
                    onBlur={() =>
                      validateAssigneeEmail(
                        getQueryData?.query?.assignee ?? '',
                        baseQueryStore.assignee,
                        baseQueryStore.setAssigneeValueState
                      )
                    }
                    showClearIcon
                    placeholder={t('display-query-adm-details-assignee-placeholder')}
                    valueState={baseQueryStore.assigneeValueState}
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('display-value-state-message-assignee')}
                      </Label>
                    }
                  />
                ) : (
                  <Text
                    aria-labelledby={t('display-query-adm-details-assignee')}
                    className={classes.formItemText}
                    data-testid="assignee"
                    wrapping
                  >
                    {formatUserName({
                      userName: getQueryData?.query?.assignedTo?.displayName,
                      sapNumber: getQueryData?.query?.assignedTo?.sapNumber ?? undefined,
                      defaultValue: baseQueryStore.assignee
                    })}
                  </Text>
                )}
                {isStatusEqual(Status.COMPLETED) || isStatusEqual(Status.CLOSED)
                  ? null
                  : getNotificationButton()}
              </FormItem>,
              [ResourceAccessLevel.INTERNAL]
            )}
            {checkAccess(
              <FormItem
                label={
                  <Label
                    aria-labelledby={t('display-query-adm-details-qe-review')}
                    id={t('display-query-adm-details-qe-review')}
                    data-help-id="display-query-adm-details-qe-review-label"
                    className={classes.formSwitchLabel}
                  >
                    {t('display-query-adm-details-qe-review')}
                  </Label>
                }
              >
                <Switch
                  role="switch"
                  disabled={!editMode}
                  onChange={onChangeQEReview}
                  checked={baseQueryStore.qeReview}
                  aria-checked={baseQueryStore.qeReview}
                  data-testid="display-query-adm-details-qe-review-switch"
                  textOff={t('display-query-adm-details-qe-review-switch-off')}
                  textOn={t('display-query-adm-details-qe-review-switch-on')}
                />
              </FormItem>,
              [ResourceAccessLevel.INTERNAL]
            )}
            {getQueryData?.query?.queryDetails?.__typename === 'MarketingQuery' && (
              <FormItem
                label={
                  <Label
                    id={t('display-query-adm-details-marketing-account')}
                    data-help-id="display-query-adm-details-marketing-account-label"
                    className={classes.formItemLabel}
                  >
                    {t('display-query-adm-details-marketing-account')}
                  </Label>
                }
              >
                <Text
                  aria-labelledby={t('display-query-adm-details-marketing-account')}
                  className={classes.formItemText}
                  data-testid="marketing-account"
                  wrapping
                >
                  {getQueryData.query.queryDetails.account}
                </Text>
              </FormItem>
            )}
          </Form>
        </Panel>
      </div>
    );
  };

  const renderAttachments = () => {
    const hasAttachments = (getQueryData?.query?.attachments || []).length > 0;

    return (
      <div data-help-id="display-query-panel-attachments">
        <Panel
          aria-labelledby="attachments-panel"
          className={classes.formContainer}
          collapsed={!hasAttachments}
          data-testid="display-query-headerdisplay-query-panel-attachments"
          header={
            <Title id="attachments-panel" level={TitleLevel.H4}>
              {t('display-query-title-attachments')}
            </Title>
          }
        >
          <Attachments
            queryId={queryId}
            queryStatus={baseQueryStore.selectedStatus}
            attachments={getQueryData?.query?.attachments || []}
          />
        </Panel>
      </div>
    );
  };

  const finalComponentView = (): JSX.Element => {
    if (fetchingQueryData) {
      return (
        <div data-testid="loading-display-query">
          <Loading size={BusyIndicatorSize.Large} />
        </div>
      );
    }

    if (errorFetchingQueryDetails?.message == '[GraphQL] Query has been deleted.') {
      return <DeletedQueryPage />;
    }

    if (!getQueryData) return <NotFoundPage />;

    return (
      <>
        <ObjectPage
          id="DisplayQueryObjectPage"
          className={classes.objectPage}
          onClick={() => setIsUserMessageOpen(false)}
          headerTitle={
            <DynamicPageTitle
              className={classes.headerTitle}
              header={<Title>{t('display-query-header-query-id', { id: queryId })}</Title>}
            />
          }
          headerContent={renderHeaderContent()}
          showTitleInHeaderContent
          alwaysShowContentHeader
          footer={
            <>
              {editMode && (
                <Bar id="edit-mode-query-div" design={BarDesign.FloatingFooter}>
                  <Button
                    data-testid="edit-save-button"
                    data-help-id="edit-save-button"
                    slot="endContent"
                    id="save-edit-query-button"
                    onClick={enableDistributionConfirmationDialog}
                    design={ButtonDesign.Emphasized}
                  >
                    {t('save')}
                  </Button>
                  <Button
                    data-testid="edit-cancel-button"
                    data-help-id="edit-cancel-button"
                    slot="endContent"
                    id="cancel-edit-query-button"
                    onClick={cancelEditQuery}
                    design={ButtonDesign.Transparent}
                  >
                    {t('cancel')}
                  </Button>
                </Bar>
              )}
            </>
          }
        >
          <ObjectPageSection id="display-query-main" className={classes.objectPageSection}>
            <FlexBox className={classes.objectPageContentWrapper}>
              <div className={classes.mainDivLeft}>
                <Prompt
                  when={!allowCancelEditing}
                  message={t('display-query-comments-leave-page-prompt')}
                />
                {renderQuestionPanel()}
                {renderDisplayQueryDetails()}
                {renderAdministrativeDetailsPanel()}
                {renderAttachments()}
                <DistributionPanel
                  fetching={fetchingQueryData}
                  distributions={sortedDistributions}
                  languages={distributionLanguages}
                  editMode={editMode}
                  isInternalUser={isInternalUser()}
                  selectedLanguages={baseQueryStore.distributions}
                  resetedDistributions={baseQueryStore.resetedDistributions}
                  setSelectedLanguages={baseQueryStore.setDistributions}
                  setResetedDistributions={baseQueryStore.setResetedDistributions}
                />
              </div>
              <div className={classes.mainDivRight}>
                <QueryConversation
                  query={getQueryData?.query}
                  isInternalUser={isInternalUser()}
                  setAllowEditModeWithoutConfirmation={setAllowEditModeWithoutConfirmation}
                  suppliers={userData?.user?.suppliers || []}
                  distributions={sortedDistributions}
                />
              </div>
            </FlexBox>
          </ObjectPageSection>
        </ObjectPage>
        <UserMessage
          isVisible={isUserMessageOpen}
          resultMessage={resultMessage}
          onResetUserMessage={() => setIsUserMessageOpen(false)}
        />
        <ConfirmationDialog
          data-testid="confirm-removing-pending-distributions"
          open={isConfirmationDialogOpen}
          type={MessageBoxTypes.Warning}
          onClose={() => setIsConfirmationDialogOpen(false)}
          onConfirm={saveEditQuery}
        >
          {t('confirm-removing-pending-distributions')}
        </ConfirmationDialog>
        <CopyEmailContentToClipboardDialog
          email={baseQueryStore.assignee}
          content={getBody()}
          isOpen={hasExceededEmailLengthLimit}
          setIsOpen={setHasExceededEmailLengthLimit}
        />
      </>
    );
  };

  return finalComponentView();
};
